import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Layout from './Layout';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/api';
import environment from '../config/environment';

const SalidaVisitante = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [visitantes, setVisitantes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [submittingId, setSubmittingId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedVisitante, setSelectedVisitante] = useState(null);
  const [selectedFirma, setSelectedFirma] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [visitanteRegistrado, setVisitanteRegistrado] = useState(null);

  const apiOrigin = useMemo(() => {
    try {
      return new URL(environment.apiUrl).origin;
    } catch (error) {
      console.warn('No se pudo obtener origin de apiUrl:', error);
      if (typeof window !== 'undefined') {
        return `${window.location.protocol}//${window.location.host}`;
      }
      return '';
    }
  }, []);

  const resolveFotoUrl = useCallback(
    (fotoPath) => {
      if (!fotoPath) return null;
      if (/^https?:\/\//i.test(fotoPath)) return fotoPath;
      if (!apiOrigin) return fotoPath;
      if (fotoPath.startsWith('/')) {
        return `${apiOrigin}${fotoPath}`;
      }
      return `${apiOrigin}/${fotoPath}`;
    },
    [apiOrigin]
  );

  const hasPuestoAsignado = useMemo(() => Boolean(user?.id_generado), [user]);

  const fetchStatus = useCallback(
    async (showLoader = true) => {
      if (!user) return;

      if (!hasPuestoAsignado) {
        setStatus(null);
        setVisitantes([]);
        setError('Tu usuario no tiene un puesto asignado. Solicita a un administrador que configure tu acceso.');
        setLoading(false);
        return;
      }

      if (showLoader) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }
      setError('');
      setSuccess('');

      try {
        const statusUrl = new URL(getApiUrl('BITACORA', 'STATUS'));
        statusUrl.searchParams.set('idGenerado', user.id_generado);
        statusUrl.searchParams.set('username', user.username || '');
        statusUrl.searchParams.set('appid', user.appid || '');

        const [statusResponse, visitantesResponse] = await Promise.all([
          fetch(statusUrl.toString()),
          (async () => {
            const visitantesUrl = new URL(getApiUrl('BITACORA', 'LISTAR_VISITANTES'));
            visitantesUrl.searchParams.set('idGenerado', user.id_generado);
            visitantesUrl.searchParams.set('username', user.username || '');
            visitantesUrl.searchParams.set('appid', user.appid || '');
            return fetch(visitantesUrl.toString());
          })()
        ]);

        const statusData = await statusResponse.json();
        const visitantesData = await visitantesResponse.json();

        if (statusResponse.ok && statusData.success) {
          setStatus(statusData.data);
        } else {
          setStatus(null);
          setError(statusData.error || 'No fue posible obtener el estado de la bitácora.');
        }

        if (visitantesResponse.ok && visitantesData.success) {
          setVisitantes(Array.isArray(visitantesData.data) ? visitantesData.data : []);
        } else {
          setVisitantes([]);
          setError((prev) => prev || visitantesData.error || 'No fue posible listar los visitantes del día.');
        }
      } catch (err) {
        console.error('Error cargando visitantes del día:', err);
        setStatus(null);
        setVisitantes([]);
        setError('Error de conexión al cargar la información de visitantes.');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [user, hasPuestoAsignado]
  );

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleRegistrarSalida = async (visitanteId) => {
    if (!status || !visitanteId) return;

    // Encontrar el visitante antes de registrar para mostrar información en el modal
    const visitante = visitantes.find(v => v.id === visitanteId);

    setSubmittingId(visitanteId);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(getApiUrl('BITACORA', 'REGISTRAR_SALIDA_VISITANTE'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          idGenerado: status.idGenerado,
          registroId: visitanteId,
          username: user?.username || '',
          appid: user?.appid || ''
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const successMessage = data.message || 'Salida registrada correctamente.';
        setSuccess(successMessage);
        setVisitanteRegistrado(visitante);
        setShowSuccessModal(true);
        // Actualizar la lista sin cerrar el modal
        await fetchStatus(false);
      } else {
        setError(data.error || 'No fue posible registrar la salida del visitante.');
      }
    } catch (err) {
      console.error('Error registrando salida de visitante:', err);
      setError('Error de conexión al registrar la salida.');
    } finally {
      setSubmittingId(null);
    }
  };

  const handleCloseSuccessModal = useCallback(() => {
    setShowSuccessModal(false);
    setSuccess('');
    setVisitanteRegistrado(null);
  }, []);

  const handleOpenFoto = (visitante) => {
    if (!visitante?.foto_url) return;
    setSelectedVisitante(visitante);
  };

  const handleCloseFoto = () => {
    setSelectedVisitante(null);
  };

  const handleOpenFirma = (visitante) => {
    if (!visitante?.foto_firma_url) return;
    setSelectedFirma(visitante);
  };

  const handleCloseFirma = () => {
    setSelectedFirma(null);
  };

  const getCategoriaLabel = (visitante) => {
    const raw = String(visitante?.categoria || '').trim().toUpperCase();
    if (raw === 'FUNCIONARIO') return 'FUNCIONARIO';
    if (raw === 'CHARLY_BODEGA') return 'BODEGA CHARLY';
    if (raw === 'TANGO_3') return 'ALFA TANGO 3';
    const obs = String(visitante?.observacion || '').trim();
    if (obs.startsWith('Bodega')) return 'BODEGA CHARLY';
    if (obs.startsWith('Tango3')) return 'ALFA TANGO 3';
    return 'VISITANTE';
  };

  return (
    <Layout>
      <div className="p-3 sm:p-4 md:p-6 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Registrar Salida</h1>
            <p className="text-gray-600">
              Consulta visitas, bodega Charly, Alfa Tango 3 y demás ingresos del día y registra su salida.
            </p>
          </div>
          <button
                type="button"
                onClick={() => fetchStatus(false)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-60"
                disabled={loading || refreshing || submittingId !== null}
          >
            {refreshing ? (
              <span className="flex items-center space-x-2">
                <svg className="animate-spin h-4 w-4 text-gray-500" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span>Actualizando...</span>
              </span>
            ) : (
              'Actualizar lista'
            )}
          </button>
        </div>

        {loading ? (
          <div className="bg-white border border-gray-200 rounded-xl p-6 flex items-center justify-center">
            <div className="flex items-center space-x-3">
              <svg className="animate-spin h-5 w-5 text-indigo-600" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className="text-sm font-medium text-gray-700">Cargando visitantes del día…</span>
            </div>
          </div>
        ) : (
          <>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4">
                <p className="text-sm font-medium">{error}</p>
              </div>
            )}

            <div className="bg-white border border-gray-200 rounded-xl shadow">
              <div className="px-4 py-3 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Personas en sitio</h2>
                  <p className="text-sm text-gray-500">
                    Personas con ingreso registrado hoy y sin salida.
                  </p>
                </div>
                <span className={`text-sm font-semibold ${visitantes.length === 0 ? 'text-green-600' : 'text-red-600'}`}>
                  Total: <span className="font-semibold">{visitantes.length}</span>
                </span>
              </div>

              {visitantes.length === 0 ? (
                <div className="px-4 py-10 text-center text-sm text-gray-500">
                  No hay personas pendientes de salida hoy.
                </div>
              ) : (
                <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {visitantes.map((visitante) => (
                    <div
                      key={visitante.id}
                      className="border border-gray-200 rounded-xl p-4 bg-gray-50 flex flex-col space-y-3 shadow-sm relative"
                    >
                      <div className="absolute top-3 right-3 flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleOpenFoto(visitante)}
                          disabled={!visitante.foto_url}
                          className="p-2 rounded-full border border-transparent text-green-600 hover:text-green-700 hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:text-gray-400 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                          title={visitante.foto_url ? 'Ver identificación' : 'Sin identificación'}
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                            />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleOpenFirma(visitante)}
                          disabled={!visitante.foto_firma_url}
                          className="p-2 rounded-full border border-transparent text-orange-500 hover:text-orange-600 hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 disabled:text-gray-400 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                          title={visitante.foto_firma_url ? 'Ver firma' : 'Sin firma registrada'}
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M3 18l6-6 4 4 8-8"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M14 7l7-3-3 7"
                            />
                          </svg>
                        </button>
                      </div>

                      <div>
                        <p className={`text-xs uppercase tracking-wide mb-1 font-semibold ${
                          getCategoriaLabel(visitante) === 'FUNCIONARIO'
                            ? 'text-amber-600'
                            : getCategoriaLabel(visitante) === 'BODEGA CHARLY'
                              ? 'text-indigo-600'
                              : getCategoriaLabel(visitante) === 'ALFA TANGO 3'
                                ? 'text-violet-600'
                                : 'text-gray-500'
                        }`}>
                          {getCategoriaLabel(visitante)}
                        </p>
                        <h3 className="text-base font-semibold text-indigo-600 break-words">
                          {visitante.p_nombre || 'Sin nombre'}
                        </h3>
                      </div>

                      <div className="flex flex-wrap gap-3 text-sm text-gray-700">
                        <div>
                          <p className="text-xs uppercase tracking-wide text-gray-500">Ingreso</p>
                          <p className="font-medium">{visitante.hora_ingreso || '—'}</p>
                        </div>
                        <div className="flex-1 min-w-[10rem]">
                          <p className="text-xs uppercase tracking-wide text-gray-500">Observación</p>
                          <p className="font-medium text-gray-700 break-words">
                            {visitante.observacion || '—'}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-2">
                        <div className="flex-1">
                          <button
                            type="button"
                            onClick={() => handleRegistrarSalida(visitante.id)}
                            className="inline-flex items-center justify-center w-full px-3 py-2 border border-transparent text-sm font-semibold rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-60"
                            disabled={submittingId === visitante.id}
                          >
                            {submittingId === visitante.id ? 'Registrando…' : 'Registrar salida'}
                          </button>
                        </div>
                      </div>

                      <div className="mt-3 pt-3 border-t border-dashed border-gray-300 text-xs text-gray-500">
                        {visitante.foto_url ? 'Toca el ícono verde para ver la identificación' : 'Sin identificación cargada'}
                        <br />
                        {visitante.foto_firma_url ? 'Usa el ícono naranja para ver la firma.' : 'Sin firma registrada.'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {selectedVisitante && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Identificación del visitante</h3>
                <p className="text-sm text-gray-500">
                  {selectedVisitante.p_nombre} · {getCategoriaLabel(selectedVisitante)}
                </p>
              </div>
              <button
                type="button"
                onClick={handleCloseFoto}
                className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 rounded-full p-1"
                aria-label="Cerrar"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="bg-gray-900">
              <img
                src={resolveFotoUrl(selectedVisitante.foto_url)}
                alt={`Identificación de ${selectedVisitante.p_nombre}`}
                className="w-full h-auto mx-auto object-contain max-h-[70vh]"
              />
            </div>
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-right">
              <button
                type="button"
                onClick={handleCloseFoto}
                className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedFirma && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Firma del visitante</h3>
                <p className="text-sm text-gray-500">{selectedFirma.p_nombre}</p>
              </div>
              <button
                type="button"
                onClick={handleCloseFirma}
                className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 rounded-full p-1"
                aria-label="Cerrar"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="bg-gray-900">
              <img
                src={resolveFotoUrl(selectedFirma.foto_firma_url)}
                alt={`Firma de ${selectedFirma.p_nombre}`}
                className="w-full h-auto mx-auto object-contain max-h-[70vh]"
              />
            </div>
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-right">
              <button
                type="button"
                onClick={handleCloseFirma}
                className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-orange-500 rounded-lg hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de éxito para salida registrada */}
      {showSuccessModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4" 
          style={{ zIndex: 9999 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              handleCloseSuccessModal();
            }
          }}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header con icono de éxito */}
            <div className="px-6 py-5 bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-100">
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                    <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900">Salida registrada</h3>
                  <p className="text-sm text-gray-600 mt-0.5">Proceso completado exitosamente</p>
                </div>
              </div>
            </div>

            {/* Contenido del modal */}
            <div className="px-6 py-5 space-y-4">
              {/* Información de la persona */}
              {visitanteRegistrado && (
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span className="text-sm font-bold text-indigo-600">{visitanteRegistrado.p_nombre || '—'}</span>
                    </div>
                    {visitanteRegistrado.hora_ingreso && (
                      <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-xs text-gray-600">Ingreso: <span className="font-medium">{visitanteRegistrado.hora_ingreso}</span></span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Mensaje de confirmación (según visitante vs funcionario) */}
              <div className="text-center">
                <p className="text-sm text-gray-700 leading-relaxed">
                  {visitanteRegistrado
                    ? getCategoriaLabel(visitanteRegistrado) === 'FUNCIONARIO'
                      ? 'La salida del funcionario ha sido registrada correctamente en la bitácora.'
                      : getCategoriaLabel(visitanteRegistrado) === 'BODEGA CHARLY'
                        ? 'La salida del ingreso de bodega Charly ha sido registrada correctamente en la bitácora.'
                        : getCategoriaLabel(visitanteRegistrado) === 'ALFA TANGO 3'
                          ? 'La salida del ingreso Alfa Tango 3 ha sido registrada correctamente en la bitácora.'
                          : 'La salida del visitante ha sido registrada correctamente en la bitácora.'
                    : success || 'La salida ha sido registrada correctamente en la bitácora.'}
                </p>
              </div>

              {/* Indicador visual */}
              <div className="flex items-center justify-center gap-2 pt-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-xs text-gray-500">Registro confirmado</span>
              </div>
            </div>

            {/* Footer con botón */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
              <button
                type="button"
                onClick={handleCloseSuccessModal}
                className="w-full inline-flex items-center justify-center px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 rounded-lg shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all duration-200"
              >
                Continuar
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default SalidaVisitante;

